import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { IBlockInvoiceListRow } from "../TableBlockInvoice";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../../shared/utils";

interface ModalBlockInvoiceProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (payload: { subject: string; message: string }) => Promise<void>;
	row: IBlockInvoiceListRow;
	isSubmitting?: boolean;
}

const defaultSubject = "Cancelamento das garantias e encerramento da parceria";

const monthMap: Record<string, string> = {
	"01": "Janeiro",
	"02": "Fevereiro",
	"03": "Março",
	"04": "Abril",
	"05": "Maio",
	"06": "Junho",
	"07": "Julho",
	"08": "Agosto",
	"09": "Setembro",
	"10": "Outubro",
	"11": "Novembro",
	"12": "Dezembro",
};

const formatReferenceLabel = (reference?: string | null) => {
	if (!reference) return "Referência não informada";

	return reference
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean)
		.map((item) => {
			const [year, month] = item.split("-");
			if (!year || !month || !monthMap[month]) return item;
			return `${monthMap[month]}/${year.slice(-2)}`;
		})
		.join(", ");
};

const buildDefaultMessage = (row: IBlockInvoiceListRow) => {
	const clientName = row.client?.name || row.client?.trade_name || "[RAZÃO SOCIAL]";
	const clientDocument = row.client?.document ? globalMaskDocument(row.client.document) : "[CNPJ]";
	const invoiceLines = row.invoices
		.map((invoice) => `${formatReferenceLabel(invoice.reference)} – ${globalFormatCurrencyBRL(invoice.value)}`)
		.join("\n");
	const normalizeLink = (value?: string | null) => {
		const trimmedValue = value?.trim();
		return trimmedValue ? trimmedValue : null;
	};
	const detailLinks = Array.from(new Set(row.invoices.map((invoice) => normalizeLink(invoice.summaryUrl) || normalizeLink(invoice.url)).filter(Boolean)));
	const detailLinksText = detailLinks.length > 0 ? detailLinks.join("\n") : "[LINK DA FATURA]";

	return [
		"Prezado(a),",
		"",
		clientName,
		clientDocument,
        "",
        "Estamos entrando em contato para comunicar que, devido ao não pagamento dos boletos descritos abaixo, e após diversas tentativas de contato, fomos obrigados a proceder com o cancelamento das garantias e o encerramento da parceria.",
		
		invoiceLines || "[RELAÇÃO DE BOLETOS] – [VALOR]",
		"",
		"Segue o link do detalhamento de garantias canceladas.",
		detailLinksText,
		"",
		"Informamos que todas as garantias da imobiliária se encontram encerradas, dentre elas a possibilidade de continuação dos sinistros e abertura dos mesmos, como também novas contratações, a não indenização dos sinistros que estão em aberto, e o bloqueio junto a plataforma.",
		"Lamentamos que a parceria tenha se encerrado dessa forma.",
	].join("\n");
};

export const ModalBlockInvoice = ({ open, onClose, onConfirm, row, isSubmitting = false }: ModalBlockInvoiceProps) => {
	const defaultMessage = useMemo(() => buildDefaultMessage(row), [row]);
	const [message, setMessage] = useState(defaultMessage);

	useEffect(() => {
		if (open) {
			setMessage(defaultMessage);
		}
	}, [defaultMessage, open]);

	const handleConfirm = async () => {
		if (!message.trim()) return;
		await onConfirm({ subject: defaultSubject, message });
	};

	return (
		<Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
					<Box>
						<Typography variant="h6">Bloquear cliente</Typography>
						<Typography variant="body2" color="text.secondary">
							Mensagem enviada antes do bloqueio para {row.client?.name || row.client?.trade_name || "cliente"}
						</Typography>
					</Box>
					<IconButton onClick={onClose} aria-label="Fechar modal" disabled={isSubmitting}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2}>
					<TextField multiline minRows={16} fullWidth label="Mensagem de bloqueio" value={message} onChange={(event) => setMessage(event.target.value)} />
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} color="inherit" disabled={isSubmitting}>
					Fechar
				</Button>
				<Button onClick={handleConfirm} variant="contained" disabled={isSubmitting || !message.trim()}>
					Bloquear
				</Button>
			</DialogActions>
		</Dialog>
	);
};